import { request } from './api';
import { requeteRh } from './apercu';
import type {
  AdresseFavorite,
  ApercuExport,
  ChronologieNoteDonnees,
  EvenementAudit,
  Pilotage,
  TableauBord,
  AdresseTrajet,
  AnalyseJustificatif,
  ApercuTrajet,
  ContexteTrajet,
  Delegation,
  LigneRegularisation,
  RecapitulatifIkDonnees,
  GroupeDepenses,
  NoteAValider,
  NoteDeFrais,
  CategorieNdf,
  CompteAnalytique,
  LigneBareme,
  MoiNdf,
  ParametreNdf,
  RefValeur,
  Depense,
  Salarie,
  StatutCompte,
  UtilisateurCourt,
  Vehicule,
  JustificatifEntrant,
} from '../types/ndf';

/**
 * Aperçu de rôle : identifiant de la personne dont on regarde l'application.
 *
 * Porté par un en-tête plutôt que par l'URL : il s'applique à TOUTES les
 * requêtes du module sans avoir à les modifier une par une, et une URL copiée
 * dans un message ne transporte pas l'aperçu par accident.
 */
// L'aperçu vit dans `services/apercu.ts`, partagé par tous les modules RH.
// Réexporté ici pour ne pas remuer les imports existants.
export { apercuActif, definirApercu } from './apercu';

/** Ajoute l'en-tête d'aperçu quand il est actif. */
const requeteNdf = requeteRh;

/**
 * Client API du module Notes de frais.
 *
 * Séparé de `api.ts` pour que le module reste isolable, mais réutilise son
 * `request` : le traitement des erreurs et la redirection sur session expirée
 * ne doivent pas diverger d'un module à l'autre.
 */

export const ndfApi = {
  moi: () => requeteNdf<MoiNdf>('/api/ndf/moi'),
  utilisateurs: () => requeteNdf<{ utilisateurs: UtilisateurCourt[] }>('/api/ndf/utilisateurs'),

  depenses: {
    liste: () => requeteNdf<{ depenses: Depense[]; total: number }>('/api/ndf/depenses'),
    creer: (body: Record<string, unknown>) =>
      request<{ depense: Depense; forcages: string[] }>('/api/ndf/depenses', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Record<string, unknown>) =>
      request<{ depense: Depense; forcages: string[] }>(`/api/ndf/depenses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    supprimer: (id: string) =>
      request<{ ok: boolean }>(`/api/ndf/depenses/${id}`, { method: 'DELETE' }),
    urlJustificatif: (id: string, type?: string) =>
      `/api/ndf/depenses/${id}/justificatif${type ? `?type=${encodeURIComponent(type)}` : ''}`,
    clients: (q: string) =>
      requeteNdf<{ clients: Array<{ id: string; nom: string }> }>(
        `/api/ndf/depenses/crm/clients?q=${encodeURIComponent(q)}`,
      ),
    missions: (clientId: string) =>
      requeteNdf<{ missions: Array<{ id: string; nom: string; type: string | null }> }>(
        `/api/ndf/depenses/crm/missions?client_id=${encodeURIComponent(clientId)}`,
      ),
  },

  /** Tableau de bord : une seule route, trois vues, des filtres communs. */
  tableauBord: (params: Record<string, string>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    return requeteNdf<TableauBord>(`/api/ndf/tableau-bord?${q.toString()}`);
  },

  /** Historique, audit et pilotage (specs §G). */
  historique: {
    note: (id: string) => requeteNdf<ChronologieNoteDonnees>(`/api/ndf/historique/note/${id}`),
    audit: (filtres: { acteur?: string; type?: string; depuis?: string; jusqua?: string }) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filtres)) if (v) q.set(k, v);
      return requeteNdf<{ evenements: EvenementAudit[]; types: string[]; tronque: boolean }>(
        `/api/ndf/historique/audit?${q.toString()}`,
      );
    },
    pilotage: (annee: number) => requeteNdf<Pilotage>(`/api/ndf/historique/pilotage?annee=${annee}`),
  },

  /** Export comptable mensuel vers l'expert-comptable. */
  exportComptable: {
    apercu: () => requeteNdf<ApercuExport>('/api/ndf/export'),
    deposer: () =>
      request<{ numero: string; chemin: string; notes: number; lignes: number }>('/api/ndf/export', {
        method: 'POST',
      }),
  },

  /** Notes de frais : le regroupement des dépenses. Les totaux viennent du serveur. */
  notes: {
    liste: () => requeteNdf<{ notes: NoteDeFrais[] }>('/api/ndf/notes'),
    detail: (id: string) =>
      requeteNdf<{ note: NoteDeFrais; depenses: Depense[]; groupes: GroupeDepenses[] }>(
        `/api/ndf/notes/${id}`,
      ),
    associables: () =>
      requeteNdf<{ depenses: Depense[]; groupes: GroupeDepenses[] }>('/api/ndf/notes/associables'),
    creer: (titre?: string | null) =>
      request<{ note: NoteDeFrais }>('/api/ndf/notes', {
        method: 'POST',
        body: JSON.stringify({ titre: titre ?? null }),
      }),
    modifier: (id: string, titre: string | null) =>
      request<{ note: NoteDeFrais }>(`/api/ndf/notes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ titre }),
      }),
    associer: (id: string, depense_ids: string[]) =>
      request<{ note: NoteDeFrais; depenses: Depense[] }>(`/api/ndf/notes/${id}/depenses`, {
        method: 'POST',
        body: JSON.stringify({ depense_ids }),
      }),
    dissocier: (id: string, depenseId: string) =>
      request<{ note: NoteDeFrais; depenses: Depense[] }>(
        `/api/ndf/notes/${id}/depenses/${depenseId}`,
        { method: 'DELETE' },
      ),
    supprimer: (id: string) =>
      request<{ ok: boolean }>(`/api/ndf/notes/${id}`, { method: 'DELETE' }),

    // ─── Circuit de validation ───────────────────────────────────────────
    soumettre: (id: string) =>
      request<{ note: NoteDeFrais }>(`/api/ndf/notes/${id}/soumettre`, { method: 'POST' }),
    approuver: (id: string) =>
      request<{ note: NoteDeFrais }>(`/api/ndf/notes/${id}/approuver`, { method: 'POST' }),
    rejeter: (id: string, body: { commentaire?: string | null; depenses?: Array<{ id: string; motif: string }> }) =>
      request<{ note: NoteDeFrais }>(`/api/ndf/notes/${id}/rejeter`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    marquerControlee: (id: string, depenseId: string) =>
      request<{ controlees: number; total: number }>(
        `/api/ndf/notes/${id}/depenses/${depenseId}/controlee`,
        { method: 'POST' },
      ),
    aValider: () => requeteNdf<{ notes: NoteAValider[] }>('/api/ndf/notes/a-valider'),
    rappeler: (id: string, motif?: string) =>
      request<{ note: NoteDeFrais }>(`/api/ndf/notes/${id}/rappeler`, {
        method: 'POST',
        body: JSON.stringify({ motif: motif ?? null }),
      }),
    rouvrir: (id: string, motif?: string) =>
      request<{ note: NoteDeFrais; avertissements: string[] }>(`/api/ndf/notes/${id}/rouvrir`, {
        method: 'POST',
        body: JSON.stringify({ motif: motif ?? null }),
      }),
    facturer: (id: string) =>
      request<{ bill_id: string; deja_existante: boolean; total: number; lignes: number }>(
        `/api/ndf/notes/${id}/facturer`,
        { method: 'POST' },
      ),

    delegations: () => requeteNdf<{ delegations: Delegation[] }>('/api/ndf/notes/delegations'),
    creerDelegation: (body: {
      utilisateur_id?: string;
      delegue_id: string;
      debut: string;
      fin: string;
      motif?: string | null;
    }) =>
      request<{ delegation: Delegation }>('/api/ndf/notes/delegations', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    supprimerDelegation: (id: string) =>
      request<{ ok: boolean }>(`/api/ndf/notes/delegations/${id}`, { method: 'DELETE' }),
  },

  /** Trajets et indemnités kilométriques. Le montant est calculé par le serveur. */
  trajets: {
    contexte: () => requeteNdf<ContexteTrajet>('/api/ndf/trajets/contexte'),
    adresses: (q: string) =>
      requeteNdf<{ adresses: AdresseTrajet[] }>(
        `/api/ndf/trajets/adresses?q=${encodeURIComponent(q)}`,
      ),
    apercu: (body: Record<string, unknown>) =>
      request<ApercuTrajet>('/api/ndf/trajets/apercu', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    creer: (body: Record<string, unknown>) =>
      request<{ depense_id: string; montant: number; distance_km: number }>('/api/ndf/trajets', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    detail: (depenseId: string) => requeteNdf<{ trajet: any }>(`/api/ndf/trajets/${depenseId}`),
    regularisation: (annee: number) =>
      requeteNdf<{ annee: number; regularisations: LigneRegularisation[] }>(
        `/api/ndf/trajets/regularisation?annee=${annee}`,
      ),
    regulariser: (salarie_id: string, annee: number) =>
      request<{ depense_id: string }>('/api/ndf/trajets/regularisation', {
        method: 'POST',
        body: JSON.stringify({ salarie_id, annee }),
      }),
    recapitulatif: (annee: number, salarieId?: string) =>
      requeteNdf<{ recapitulatif: RecapitulatifIkDonnees }>(
        `/api/ndf/trajets/recapitulatif?annee=${annee}${salarieId ? `&salarie_id=${salarieId}` : ''}`,
      ),
    ajouterFavori: (body: { libelle_court: string; libelle: string; latitude: number; longitude: number }) =>
      request<{ favori: AdresseFavorite }>('/api/ndf/trajets/favoris', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    retirerFavori: (id: string) =>
      request<{ ok: boolean }>(`/api/ndf/trajets/favoris/${id}`, { method: 'DELETE' }),
  },

  /** Lecture d'un justificatif par l'IA — asynchrone : on lance, puis on suit. */
  analyses: {
    lancer: (justificatifs: JustificatifEntrant[]) =>
      request<{ id: string; statut: string }>('/api/ndf/analyses', {
        method: 'POST',
        body: JSON.stringify({ justificatifs }),
      }),
    suivre: (id: string) => requeteNdf<AnalyseJustificatif>(`/api/ndf/analyses/${id}`),
  },

  salaries: {
    liste: (inactifs = false) =>
      requeteNdf<{ salaries: Salarie[]; total: number }>(
        `/api/ndf/salaries${inactifs ? '?inactifs=1' : ''}`,
      ),
    detail: (id: string) => requeteNdf<{ salarie: Salarie }>(`/api/ndf/salaries/${id}`),
    creer: (body: Partial<Salarie>) =>
      request<{ salarie: Salarie }>('/api/ndf/salaries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<Salarie>) =>
      request<{ salarie: Salarie }>(`/api/ndf/salaries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  vehicules: {
    liste: (salarieId: string) =>
      requeteNdf<{ vehicules: Vehicule[]; total: number }>(
        `/api/ndf/vehicules?salarie_id=${encodeURIComponent(salarieId)}`,
      ),
    creer: (body: Partial<Vehicule>) =>
      request<{ vehicule: Vehicule }>('/api/ndf/vehicules', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<Vehicule>) =>
      request<{ vehicule: Vehicule }>(`/api/ndf/vehicules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  listes: {
    lire: (liste?: string) =>
      requeteNdf<{ valeurs: RefValeur[] }>(
        `/api/ndf/referentiels/listes${liste ? `?liste=${encodeURIComponent(liste)}` : ''}`,
      ),
    creer: (body: Partial<RefValeur>) =>
      request<{ valeur: RefValeur }>('/api/ndf/referentiels/listes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<RefValeur>) =>
      request<{ valeur: RefValeur }>(`/api/ndf/referentiels/listes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  categories: {
    lire: () => requeteNdf<{ categories: CategorieNdf[] }>('/api/ndf/referentiels/categories'),
    creer: (body: Partial<CategorieNdf>) =>
      request<{ categorie: CategorieNdf }>('/api/ndf/referentiels/categories', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<CategorieNdf>) =>
      request<{ categorie: CategorieNdf }>(`/api/ndf/referentiels/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  comptes: {
    lire: () => requeteNdf<{ comptes: CompteAnalytique[] }>('/api/ndf/referentiels/comptes'),
    modifier: (id: string, body: Partial<CompteAnalytique>) =>
      request<{ compte: CompteAnalytique }>(`/api/ndf/referentiels/comptes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  bareme: {
    lire: (annee: number) =>
      requeteNdf<{ bareme: LigneBareme[] }>(`/api/ndf/referentiels/bareme?annee=${annee}`),
    /** Toutes les années — sert à connaître celles réellement saisies. */
    lireTout: () => requeteNdf<{ bareme: LigneBareme[] }>('/api/ndf/referentiels/bareme'),
    modifier: (id: string, body: Partial<LigneBareme>) =>
      request<{ ligne: LigneBareme }>(`/api/ndf/referentiels/bareme/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    dupliquer: (annee_source: number, annee_cible: number, source_officielle?: string) =>
      request<{ ok: boolean; lignes_creees: number }>('/api/ndf/referentiels/bareme/dupliquer', {
        method: 'POST',
        body: JSON.stringify({ annee_source, annee_cible, source_officielle }),
      }),
  },

  parametres: {
    lire: () => requeteNdf<{ parametres: ParametreNdf[] }>('/api/ndf/referentiels/parametres'),
    enregistrer: (cle: string, valeur: string) =>
      request<{ ok: boolean }>('/api/ndf/referentiels/parametres', {
        method: 'PUT',
        body: JSON.stringify({ cle, valeur }),
      }),
  },
};
